import { css } from '@emotion/react'
import { Fragment, memo, SyntheticEvent, useMemo } from 'react'
import type { EmojiClubs, Message, UserBadge } from '../../../../../types/mfc'
import { QUICK_REPLY } from '../../../../constants/floaters'
import { tokensQualifyPurchase } from '../../../../mfcLogic/tokens'
import { generateModalKey } from '../../../../state/modals'
import { slugToID } from '../../../../state/state.users'
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState'
import { useFloater } from '../../../Hooks/useModal'
import { useScreenStateSelector } from '../../../Hooks/useScreenState'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, { maxRoomWidth } from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import RoomMessage from './RoomMessage'

const commonStyles = css({
    position: 'relative',
    padding: 0,
    fontSize: '0.75rem', // 12px (but responsive)
    fontWeight: 500,
    textShadow: 'none',
    borderRadius: 0,
    wordBreak: 'break-word',
    margin: `${getThemeSpacing(1)} ${getThemeSpacing(3)}`,
    [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
        margin: `${getThemeSpacing(1)} ${getThemeSpacing(2.25)}`,
    },
})
const commonSystemStyles = css({
    width: '100%',
    backgroundColor: 'transparent',
    margin: 0,
    letterSpacing: '0.00938em',
    lineHeight: 1.5,
    fontSize: '.8rem',
    fontWeight: '500',
    textAlign: 'center',
    borderRadius: 0,
    clear: 'both',
})
const commonStylesFullWindow = css({
    fontWeight: 700,
    textShadow:
        '0.5px 0 0 #000, -0.5px 0 0 #000, 0 -0.5px 0 #000, 0 0.5px 0 #000',
    margin: `${getThemeSpacing(0.625)} ${getThemeSpacing(1.5)}`,
    [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
        margin: `${getThemeSpacing(0.625)} ${getThemeSpacing(1)}`,
    },
})
const commonSystemStylesFullWindow = css({
    fontWeight: 700,
    textShadow: '1px 0 0 #000, -1px 0 0 #000, 0 -1px 0 #000, 0 1px 0 #000',
})
const commonLinkStyles = css({
    '& > a': {
        textDecoration: 'underline',
        cursor: 'pointer',
    },
})

const meStyles = css({
    fontWeight: 500,
})
const theyStyles = css({
    position: 'relative',
})
const sarcasmStyles = css({
    display: 'inline-block',
    transform: 'skew(20deg)',
    WebkitTransform: 'skew(20deg)',
    MozTransform: 'skew(20deg)',
    msTransform: 'skew(20deg)',
    OTransform: 'skew(20deg)',
})
const emoteStyles = css({
    fontSize: '.75rem',
    fontStyle: 'italic',
})
const adminStyles = css({
    backgroundColor: palette.muiLtGreen50,
    color: '#000',
    fontWeight: 'bolder',
    padding: getThemeSpacing(1),
})
const adminStylesFullWindow = css({
    backgroundColor: 'rgba(200, 255, 138, 0.15)',
    color: palette.commonWhite,
    fontWeight: 700,
})
const clubShowStyles = css({
    backgroundColor: palette.muiBlue50,
    color: palette.muiBlue800,
    padding: getThemeSpacing(1),
    '& a': {
        color: `${palette.muiGreen800} !important`,
    },
})
const clubShowStylesFullWindow = css({
    backgroundColor: 'rgba(0, 107, 184, 0.15)',
    color: palette.commonWhite,
})
const tipStyles = css({
    width: '100%',
    margin: 0,
    padding: `${getThemeSpacing(1)} ${getThemeSpacing(3)}`,
    fontWeight: 600,
    textAlign: 'center',
    [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
        padding: `${getThemeSpacing(1)} ${getThemeSpacing(2.25)}`,
    },
    '& a': {
        color: 'inherit !important',
    },
})
const tipStylesFullWindow = css({
    width: 'unset',
    margin: `${getThemeSpacing(0.625)} ${getThemeSpacing(1.5)}`,
    padding: 0,
    fontWeight: 700,
    textAlign: 'left',
    [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
        padding: 0,
    },
})

const warningStyles = css({
    backgroundColor: palette.muiYellow50,
    color: palette.muiYellow800,
    padding: getThemeSpacing(1),
})
const warningStylesFullWindow = css({
    backgroundColor: 'rgba(255, 241, 87, 0.15)',
    color: palette.commonWhite,
})
const errorStyles = css({
    color: palette.chatSystemMessage,
    padding: getThemeSpacing(1),
    '& > a': {
        color: `${palette.chatSystemMessage} !important`,
        paddingRight: getThemeSpacing(0.5),
    },
})
const errorStylesFullWindow = css({
    color: palette.commonWhite,
})

const getMessageStyles = (fullWindow: boolean) => ({
    me: [
        commonStyles,
        meStyles,
        ...(fullWindow ? [commonStylesFullWindow] : []),
    ],
    they: [
        commonStyles,
        theyStyles,
        ...(fullWindow ? [commonStylesFullWindow] : []),
    ],
    message: [commonStyles, ...(fullWindow ? [commonStylesFullWindow] : [])],
    sarcasm: [
        commonStyles,
        sarcasmStyles,
        ...(fullWindow ? [commonSystemStylesFullWindow] : []),
    ],
    emote: [
        commonStyles,
        emoteStyles,
        ...(fullWindow ? [commonSystemStylesFullWindow] : []),
    ],
    confirmation: [
        commonSystemStyles,
        css({
            color: !fullWindow ? palette.primary : palette.commonWhite,
            padding: getThemeSpacing(1),
        }),
        ...(fullWindow ? [commonSystemStylesFullWindow] : []),
    ],
    warning: [
        commonSystemStyles,
        warningStyles,
        ...(fullWindow
            ? [commonSystemStylesFullWindow, warningStylesFullWindow]
            : []),
    ],
    error: [
        commonSystemStyles,
        commonLinkStyles,
        errorStyles,
        css({
            backgroundColor: !fullWindow
                ? palette.chatBackground
                : 'rgba(0, 0, 0, 0.4)',
        }),
        ...(fullWindow
            ? [commonSystemStylesFullWindow, errorStylesFullWindow]
            : []),
    ],
    system: [
        commonSystemStyles,
        commonLinkStyles,
        css({
            padding: getThemeSpacing(1),
            overflowWrap: 'break-word',
            backgroundColor: palette.chatBackground,
            '& a': {
                color: `${palette.systemMessageLink} !important`,
            },
        }),
        ...(fullWindow ? [commonSystemStylesFullWindow] : []),
    ],
    adminAnnouncement: [
        commonSystemStyles,
        commonLinkStyles,
        adminStyles,
        css({
            '& a': {
                color: `${palette.systemMessageLink} !important`,
            },
        }),
        ...(fullWindow
            ? [commonSystemStylesFullWindow, adminStylesFullWindow]
            : []),
    ],
    clubShow: [
        commonSystemStyles,
        commonLinkStyles,
        clubShowStyles,
        ...(fullWindow
            ? [commonSystemStylesFullWindow, clubShowStylesFullWindow]
            : []),
    ],
    tip: [
        commonSystemStyles,
        commonLinkStyles,
        tipStyles,
        css({
            backgroundColor: !fullWindow
                ? palette.chatTipBackground
                : palette.overlayTipBackground,
            color: !fullWindow ? palette.chatTip : palette.overlayTip,
        }),
        ...(fullWindow
            ? [commonSystemStylesFullWindow, tipStylesFullWindow]
            : []),
    ],
})

const fakeMessageStyles = css({
    display: 'inline-block',
    width: '100%',
    opacity: 0,
    '& div,span': {
        lineHeight: 0,
        height: 0,
        margin: '0 200px',
        padding: '0 200px',
    },
})

interface RoomMessagesProps {
    userBadges?: Array<UserBadge>
    emojiClubs?: EmojiClubs
    slug: string
    fullWindow: boolean
    darkMode: boolean
    isLounge: boolean
    pinned?: boolean
    messages: Message[]
    clubshowActive: boolean
    onLoad(event: SyntheticEvent<HTMLImageElement>): void
    onOpenMenu(slug: string): void
    onOpenWhiteboard(identifier: string | number): void
}

const RoomMessages = memo((props: RoomMessagesProps) => {
    const {
        slug,
        fullWindow,
        darkMode,
        pinned,
        isLounge,
        messages,
        userBadges,
        emojiClubs,
        clubshowActive,
        onLoad,
        onOpenMenu,
        onOpenWhiteboard,
    } = props

    const [quickReply] = useFloater(
        generateModalKey(QUICK_REPLY, slugToID[slug]),
    )
    const isQuickReplyOpen =
        quickReply && quickReply.show && !quickReply.state.minimized
    const styles = useMemo(() => getMessageStyles(fullWindow), [fullWindow])
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')
    const vertical = useScreenStateSelector('portrait')

    const renderMessage = (message: Message, index: number) => (
        <RoomMessage
            slug={slug}
            fullWindow={fullWindow}
            key={`room-message-${slug}-${index}`}
            styles={styles}
            message={message}
            isLounge={isLounge}
            darkMode={darkMode}
            pinned={pinned}
            isQuickReplyOpen={isQuickReplyOpen}
            isMobile={isMobile}
            isVertical={vertical}
            userBadges={userBadges}
            emojiClubs={emojiClubs}
            clubshowActive={clubshowActive}
            onLoad={onLoad}
            onOpenMenu={onOpenMenu}
            onOpenWhiteboard={onOpenWhiteboard}
            onQualifyPurchase={tokensQualifyPurchase}
        />
    )

    return (
        <Fragment>
            {pinned && ( // this ensures a pinned room fully expands even when chat is nearly empty
                <div css={fakeMessageStyles}>
                    <div>
                        <span></span>
                    </div>
                </div>
            )}
            {messages.map(renderMessage)}
        </Fragment>
    )
})

export default RoomMessages
